import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Send, CheckCircle, Loader2, Plus, Trash2 } from 'lucide-react';
import emailjs from '@emailjs/browser';
import URLGuide from './URLGuide';

const QuoteForm = ({ language, translations }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });

  const [products, setProducts] = useState([
    { productUrl: '', description: '', quantity: '1' }
  ]);
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleProductChange = (index, field, value) => {
    const newProducts = [...products];
    newProducts[index][field] = value;
    setProducts(newProducts);
  };

  const addProduct = () => {
    setProducts([...products, { productUrl: '', description: '', quantity: '1' }]);
  };

  const removeProduct = (index) => {
    if (products.length > 1) {
      const newProducts = products.filter((_, i) => i !== index);
      setProducts(newProducts);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.address) {
      toast.error(language === 'en' ? 'Please fill in all required fields' : 'Por favor complete todos los campos requeridos');
      return;
    }

    // Validate at least one product URL
    const hasValidProduct = products.some(p => p.productUrl.trim() !== '');
    if (!hasValidProduct) {
      toast.error(language === 'en' ? 'Please add at least one product URL' : 'Por favor agregue al menos una URL de producto');
      return;
    }

    setIsSubmitting(true);

    try {
      // Format products list for email
      const productsList = products
        .filter(p => p.productUrl.trim() !== '')
        .map((p, i) => 
          `Product ${i + 1}: ${p.productUrl} | Qty: ${p.quantity} | Notes: ${p.description || 'N/A'}`
        ).join('\n');

      // EmailJS configuration - simplified template params
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        products_list: productsList,
        delivery_address: formData.address,
        message: `New quote request from ${formData.name}\n\nProducts:\n${productsList}\n\nDelivery Address:\n${formData.address}`
      };

      // Send email using EmailJS
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      // Optional: send an automatic confirmation email to the customer.
      // Activates only when a confirmation template ID is configured.
      const confirmationTemplateId = process.env.REACT_APP_EMAILJS_CONFIRMATION_TEMPLATE_ID;
      if (confirmationTemplateId) {
        try {
          await emailjs.send(
            process.env.REACT_APP_EMAILJS_SERVICE_ID,
            confirmationTemplateId,
            {
              to_name: formData.name,
              to_email: formData.email,
              products_list: productsList,
              delivery_address: formData.address
            },
            process.env.REACT_APP_EMAILJS_PUBLIC_KEY
          );
        } catch (confirmErr) {
          // Confirmation is a nice-to-have; never block the main request on it
          console.error('Confirmation email failed:', confirmErr);
        }
      }

      setIsSubmitted(true);
      toast.success(translations.success);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: ''
        });
        setProducts([{ productUrl: '', description: '', quantity: '1' }]);
        setIsSubmitted(false);
      }, 3000);

    } catch (error) {
      console.error('EmailJS Error:', error);
      toast.error(
        language === 'en' 
          ? 'Failed to submit request. Please try again or contact us directly.' 
          : 'Error al enviar solicitud. Inténtelo de nuevo o contáctenos directamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Card className="border-2 border-[#3dd3ee]">
        <CardContent className="p-12 text-center">
          <CheckCircle className="w-16 h-16 text-[#3dd3ee] mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-[#1e1919] mb-2">{translations.success}</h3>
          <p className="text-[#736c64]">
            {language === 'en' 
              ? "We'll review your request and get back to you within 24 hours." 
              : "Revisaremos su solicitud y nos comunicaremos con usted en 24 horas."}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-[#8b1538]/20 shadow-xl">
      <CardHeader className="bg-gradient-to-r from-[#8b1538]/5 to-[#fad24b]/5">
        <CardTitle className="text-3xl font-bold text-[#1e1919] text-center">
          {translations.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="name" className="text-[#1e1919] font-semibold">
                {translations.name} *
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="mt-2 border-[#736c64]/30 focus:border-[#8b1538]"
                placeholder={language === 'en' ? 'John Doe' : 'Juan Pérez'}
              />
            </div>
            
            <div>
              <Label htmlFor="email" className="text-[#1e1919] font-semibold">
                {translations.email} *
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="mt-2 border-[#736c64]/30 focus:border-[#8b1538]"
                placeholder="example@email.com"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="phone" className="text-[#1e1919] font-semibold">
              {translations.phone} *
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              required
              className="mt-2 border-[#736c64]/30 focus:border-[#8b1538]"
              placeholder="(501) XXX-XXXX"
            />
          </div>

          {/* Products Section */}
          <div className="border-2 border-[#fad24b]/30 rounded-lg p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1e1919]">
                {language === 'en' ? 'Products' : 'Productos'}
              </h3>
              <span className="text-sm text-[#736c64]">
                {language === 'en' ? `${products.length} item(s)` : `${products.length} artículo(s)`}
              </span>
            </div>

            {products.map((product, index) => (
              <div key={index} className="space-y-4 p-4 bg-[#f7f5f2]/50 rounded-lg relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-[#8b1538]">
                    {language === 'en' ? `Product ${index + 1}` : `Producto ${index + 1}`}
                  </span>
                  {products.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeProduct(index)}
                      className="text-red-600 hover:text-red-800 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[#1e1919] font-semibold">
                      {translations.productUrl} *
                    </Label>
                    <URLGuide language={language} />
                  </div>
                  <Input
                    type="url"
                    value={product.productUrl}
                    onChange={(e) => handleProductChange(index, 'productUrl', e.target.value)}
                    required
                    className="mt-2 border-[#736c64]/30 focus:border-[#8b1538]"
                    placeholder="https://www.amazon.com/product..."
                  />
                </div>

                <div>
                  <Label className="text-[#1e1919] font-semibold">
                    {translations.description}
                  </Label>
                  <Textarea
                    value={product.description}
                    onChange={(e) => handleProductChange(index, 'description', e.target.value)}
                    rows={2}
                    className="mt-2 border-[#736c64]/30 focus:border-[#8b1538]"
                    placeholder={language === 'en' ? 'Size, color, specifications...' : 'Talla, color, especificaciones...'}
                  />
                </div>

                <div className="w-32">
                  <Label className="text-[#1e1919] font-semibold">
                    {translations.quantity} *
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    value={product.quantity}
                    onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                    required
                    className="mt-2 border-[#736c64]/30 focus:border-[#8b1538]"
                  />
                </div>
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              onClick={addProduct}
              className="w-full border-2 border-[#8b1538] text-[#8b1538] hover:bg-[#8b1538] hover:text-white transition-all"
            >
              <Plus className="w-4 h-4 mr-2" />
              {language === 'en' ? 'Add Another Product' : 'Agregar Otro Producto'}
            </Button>
          </div>

          <div>
            <Label htmlFor="address" className="text-[#1e1919] font-semibold">
              {translations.address} *
            </Label>
            <Textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              rows={3}
              className="mt-2 border-[#736c64]/30 focus:border-[#8b1538]"
              placeholder={language === 'en' ? 'Full delivery address in Belize' : 'Dirección completa de entrega en Belice'}
            />
          </div>

          <Button 
            type="submit" 
            size="lg"
            disabled={isSubmitting}
            className="w-full bg-[#8b1538] hover:bg-[#6b0f2a] text-white py-6 text-lg font-semibold transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {language === 'en' ? 'Sending...' : 'Enviando...'}
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                {translations.submit}
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default QuoteForm;
